import { ExperienceStyles, type ExperienceTheme } from '@/types/gql.generated';
import type { HSL } from '../types';

export const hasColor = (theme: ExperienceTheme): boolean => {
  return Boolean(theme.colorType);
};

export type BaseStyleItem = {
  id: string;
  name: string;
  type: ExperienceStyles; // 'Color' | 'Gradient' | 'Block' | 'Scene'
  color: HSL;
};

export type GradientSwatch = BaseStyleItem & {
  gradient: string;
  colors?: string[];
  direction?: number;
};

export type BlockStyle = BaseStyleItem & {
  backgroundColor: string;
  accentColor?: string;
  borderRadius: string;
  shadow: string;
  border?: string;
};

export type SceneStyle = GradientSwatch & {
  foregroundColor?: string;
  accentColor?: string;
  backgroundImageId?: string;
  backgroundPosition?: [number, number]; // top, left
  sparkleId?: string;
  patternId?: string;
  patternSize?: string; // e.g., '300px'
  patternColor?: string;
};

export type PatternStyle = GradientSwatch & {
  patternId: string;
  patternSize?: string; // e.g., '300px'
  patternColor?: string;
};

export type StyleItem =
  BaseStyleItem | GradientSwatch | BlockStyle | SceneStyle;

export type StylePanelProps = {
  onStyleSelect?: (style: StyleItem) => void;
  onSparkleToggle?: (enabled: boolean) => void;
};

// Colors, Gradients, Blocks, Scenes, Patterns

// Color Styles Configuration
export const colorSwatches: BaseStyleItem[] = [
  {
    id: 'ah-red',
    name: 'Agenda Hero Red',
    type: ExperienceStyles.Color,
    color: { hue: 346, saturation: 80, lightness: 52 }, // red
  },
  {
    id: 'ah-orange',
    name: 'Agenda Hero Orange',
    type: ExperienceStyles.Color,
    color: { hue: 20, saturation: 86, lightness: 55 }, // orange
  },
  {
    id: 'ah-yellow',
    name: 'Agenda Hero Yellow',
    type: ExperienceStyles.Color,
    color: { hue: 44, saturation: 100, lightness: 63 }, // yellow
  },
  {
    id: 'ah-green',
    name: 'Agenda Hero Green',
    type: ExperienceStyles.Color,
    color: { hue: 169, saturation: 58, lightness: 30 }, // green
  },
  {
    id: 'ah-blue',
    name: 'Agenda Hero Blue',
    type: ExperienceStyles.Color,
    color: { hue: 208, saturation: 69, lightness: 51 }, // blue
  },
  {
    id: 'ah-purple',
    name: 'Agenda Hero Purple',
    type: ExperienceStyles.Color,
    color: { hue: 257, saturation: 64, lightness: 58 }, // purple
  },
  {
    id: 'ah-pink',
    name: 'Agenda Hero Pink',
    type: ExperienceStyles.Color,
    color: { hue: 336, saturation: 100, lightness: 68 }, // pink
  },
  {
    id: 'ah-gray',
    name: 'Agenda Hero Gray',
    type: ExperienceStyles.Color,
    color: { hue: 0, saturation: 0, lightness: 53 }, // gray
  },
  {
    id: 'ah-brown',
    name: 'Agenda Hero Brown',
    type: ExperienceStyles.Color,
    color: { hue: 35, saturation: 42, lightness: 54 }, // brown
  },
  {
    id: 'ah-white',
    name: 'Agenda Hero White',
    type: ExperienceStyles.Color,
    color: { hue: 0, saturation: 0, lightness: 100 }, // white
  },
];

// Gradient Styles Configuration
export const gradientSwatches: GradientSwatch[] = [
  {
    id: 'candy-sky',
    name: 'Candy Sky',
    type: ExperienceStyles.Gradient,
    gradient: 'linear-gradient(180deg, #F0B6ED 0%, #8BD8EC 100%)',
    color: { hue: 290, saturation: 70, lightness: 80 }, // #F0B6ED
    colors: ['#F0B6ED', '#8BD8EC'],
    direction: 180,
  },
  {
    id: 'berry-fade',
    name: 'Berry Fade',
    type: ExperienceStyles.Gradient,
    gradient: 'linear-gradient(180deg, #D595D9 0%, #F7AFB7 100%)',
    color: { hue: 296, saturation: 47, lightness: 72 }, // hsla(296, 47%, 72%, 1.00)
    colors: ['#D595D9', '#F7AFB7'],
    direction: 180,
  },
  {
    id: 'lavender-mist',
    name: 'Lavender Mist',
    type: ExperienceStyles.Gradient,
    gradient: 'linear-gradient(180deg, #D08FDD 0%, #D3DDFD 100%)',
    color: { hue: 270, saturation: 60, lightness: 75 }, // #D08FDD
    colors: ['#D08FDD', '#D3DDFD'],
    direction: 180,
  },
  {
    id: 'mermaid',
    name: 'Mermaid',
    type: ExperienceStyles.Gradient,
    gradient: 'linear-gradient(180deg, #65D5B5 0%, #93C3EB 100%)',
    color: { hue: 195, saturation: 100, lightness: 70 }, // #4facfe
    colors: ['#65D5B5', '#93C3EB'],
    direction: 180,
  },
  {
    id: 'cool-drift',
    name: 'Cool Drift',
    type: ExperienceStyles.Gradient,
    gradient: 'linear-gradient(180deg, #9DA4EC 0%, #8EDDF1 100%)',
    color: { hue: 210, saturation: 100, lightness: 75 }, // #89f7fe
    colors: ['#89f7fe', '#66a6ff'],
    direction: 180,
  },
  {
    id: 'indigo-calm',
    name: 'Indigo Calm',
    type: ExperienceStyles.Gradient,
    gradient: 'linear-gradient(180deg, #6181EB 0%, #A582C8 100%)',
    color: { hue: 229, saturation: 76, lightness: 66 }, // #6181EB
    colors: ['#6181EB', '#A582C8'],
    direction: 180,
  },
  {
    id: 'purple-dream',
    name: 'Purple Dream',
    type: ExperienceStyles.Gradient,
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: { hue: 229, saturation: 76, lightness: 66 }, // #667eea
    colors: ['#667eea', '#764ba2'],
    direction: 135,
  },
  {
    id: 'warm-dusk',
    name: 'Warm Dusk',
    type: ExperienceStyles.Gradient,
    gradient: 'linear-gradient(180deg, #E9BE68 0%, #E373B0 50%, #41327F 100%)',
    color: { hue: 40, saturation: 70, lightness: 60 }, // #E9BE68
    colors: ['#E9BE68', '#E373B0', '#41327F'],
    direction: 180,
  },
  {
    id: 'creamsicle',
    name: 'Creamsicle',
    type: ExperienceStyles.Gradient,
    gradient: 'linear-gradient(180deg, #F0C792 0%, #EE915B 100%)',
    color: { hue: 34, saturation: 76, lightness: 76 }, // hsla(34, 76%, 76%, 1.00)
    colors: ['#F0C792', '#EE915B'],
    direction: 180,
  },
  {
    id: 'ocean-breeze',
    name: 'Ocean Breeze',
    type: ExperienceStyles.Gradient,
    gradient: `
        radial-gradient(at 40% 20%, #D08FDD 0px, transparent 50%),
        radial-gradient(at 80% 0%, #D3DDFD 0px, transparent 50%),
        radial-gradient(at 80% 50%, #d3fcfdff 0px, transparent 50%),
        radial-gradient(at 0% 50%, #dea6ffff 0px, transparent 50%)
    `,
    color: { hue: 195, saturation: 100, lightness: 70 }, // #4facfe
    colors: ['#4facfe', '#00f2fe'],
    direction: 135,
  },
  {
    id: 'cyan-blue',
    name: 'Arctic Ice',
    type: ExperienceStyles.Gradient,
    gradient: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    color: { hue: 210, saturation: 100, lightness: 75 }, // #89f7fe
    colors: ['#89f7fe', '#66a6ff'],
    direction: 135,
  },
  {
    id: 'purple-rain',
    name: 'Purple Rain',
    type: ExperienceStyles.Gradient,
    gradient: `
      radial-gradient(ellipse 800px 600px at top left, rgba(232, 213, 255, 0.8) 0%, transparent 50%),
      radial-gradient(ellipse 600px 800px at bottom right, rgba(124, 58, 237, 0.9) 0%, transparent 60%),
      linear-gradient(135deg, #f3e8ff 0%, #ddd6fe 30%, #c4b5fd 60%, #a78bfa 80%, #7c3aed 100%)
    `,
    color: { hue: 270, saturation: 60, lightness: 75 }, // #f3e8ff
    colors: ['#f3e8ff', '#ddd6fe', '#c4b5fd', '#a78bfa', '#7c3aed'],
    direction: 135,
  },
];

// Block Styles Configuration
export const blockStyles: BlockStyle[] = [
  {
    id: 'rounded-soft',
    name: 'Soft Rounded',
    type: ExperienceStyles.Block,
    color: { hue: 210, saturation: 100, lightness: 60 }, // blue
    backgroundColor: '#ffffff',
    accentColor: '#3182ce',
    borderRadius: '12px',
    shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  {
    id: 'sharp-modern',
    name: 'Sharp Modern',
    type: ExperienceStyles.Block,
    color: { hue: 210, saturation: 100, lightness: 60 }, // blue
    backgroundColor: '#ffffff',
    borderRadius: '4px',
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
  },
  {
    id: 'pill-shaped',
    name: 'Pill Shaped',
    type: ExperienceStyles.Block,
    color: { hue: 210, saturation: 100, lightness: 60 }, // blue
    backgroundColor: '#ffffff',
    borderRadius: '9999px',
    shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  {
    id: 'glass-morphism',
    name: 'Glass Effect',
    type: ExperienceStyles.Block,
    color: { hue: 210, saturation: 100, lightness: 60 }, // blue
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    borderRadius: '16px',
    shadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
    border: '1px solid rgba(255, 255, 255, 0.18)',
  },
  {
    id: 'minimal-flat',
    name: 'Minimal Flat',
    type: ExperienceStyles.Block,
    color: { hue: 210, saturation: 100, lightness: 60 }, // blue
    backgroundColor: '#f7fafc',
    borderRadius: '8px',
    shadow: 'none',
    border: '1px solid #e2e8f0',
  },
  {
    id: 'elevated-card',
    name: 'Elevated Card',
    type: ExperienceStyles.Block,
    color: { hue: 210, saturation: 100, lightness: 60 }, // blue
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    shadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  {
    id: 'neumorphism',
    name: 'Neumorphism',
    type: ExperienceStyles.Block,
    color: { hue: 210, saturation: 100, lightness: 60 }, // blue
    backgroundColor: '#e0e0e0',
    borderRadius: '20px',
    shadow: '20px 20px 60px #bebebe, -20px -20px 60px #ffffff',
  },
  {
    id: 'brutalist',
    name: 'Brutalist',
    type: ExperienceStyles.Block,
    color: { hue: 0, saturation: 0, lightness: 0 }, // black
    backgroundColor: '#000000',
    borderRadius: '0px',
    shadow: '8px 8px 0px #ff0000',
    border: '4px solid #ffffff',
  },
];

// Scene Styles Configuration
export const sceneStyles: SceneStyle[] = [
  {
    id: 'birthday',
    name: 'Birthday',
    type: ExperienceStyles.Scene,
    color: { hue: 210, saturation: 100, lightness: 60 }, // blue
    colors: ['#4693D5', '#B5D4EF'],
    gradient: 'linear-gradient(180deg, #4693D5 0%, #B5D4EF 100%)',
    backgroundImageId: 'birthday.svg',
    backgroundPosition: [-50, 0],
    direction: 180,
    sparkleId: 'confetti',
  },
  {
    id: 'school-spirit',
    name: 'School Spirit',
    type: ExperienceStyles.Scene,
    gradient: `
      linear-gradient(180deg, #48A175 0%, rgba(72, 161, 117, 0.50) 100%), #FFF
    `,
    color: { hue: 160, saturation: 70, lightness: 60 }, // #48A175
    colors: ['#48A175', 'rgba(72, 161, 117, 0.50)'],
    patternId: 'school.svg',
    patternSize: '300px',
  },
  {
    id: 'flowers',
    name: 'Sunset Vibes',
    type: ExperienceStyles.Scene,
    color: { hue: 340, saturation: 100, lightness: 70 }, // pink
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    foregroundColor: '#2d3748',
    sparkleId: 'flowers',
    patternSize: '900px',
    accentColor: '#f56565',
  },
  {
    id: 'soccer',
    name: 'Corporate Clean',
    type: ExperienceStyles.Scene,
    color: { hue: 210, saturation: 100, lightness: 60 }, // blue
    gradient: 'linear-gradient(135deg, #069cffff 0%, rgba(22, 59, 97, 1) 100%)',
    colors: ['#069cffff', 'rgba(22, 59, 97, 1)'],
    foregroundColor: '#2d3748',
    patternId: 'pets',
    accentColor: '#3182ce',
  },
  {
    id: 'guitar',
    name: 'Neon Cyber',
    type: ExperienceStyles.Scene,
    color: { hue: 280, saturation: 100, lightness: 70 }, // purple
    gradient: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
    patternId: 'science.svg',
    patternSize: '400px',
    foregroundColor: '#00ff88',
    accentColor: '#ff0080',
  },
  {
    id: 'pink-heart',
    name: 'Heartfelt',
    type: ExperienceStyles.Scene,
    color: { hue: 0, saturation: 100, lightness: 70 }, // red
    gradient: `
    linear-gradient(180deg, #F13538 0%, rgba(241, 53, 56, 0.50) 100%), #FFF;
    `,
    colors: ['#F13538', 'rgba(241, 53, 56, 0.50)'],
    patternId: 'hearts.svg',
    patternSize: '600px',
    foregroundColor: '#F13538',
    accentColor: '#ff8df2ff',
  },
  {
    id: 'lightning',
    name: 'Desert Sand',
    type: ExperienceStyles.Scene,
    color: { hue: 30, saturation: 100, lightness: 70 }, // orange
    gradient: 'linear-gradient(135deg, #fed7aa 0%, #f6ad55 100%)',
    patternId: 'travel.svg',
    patternSize: '900px',
    foregroundColor: '#744210',
    accentColor: '#dd6b20',
  },
  {
    id: 'unicorns',
    name: 'Electric Purple',
    type: ExperienceStyles.Scene,
    color: { hue: 290, saturation: 100, lightness: 60 }, // purple
    gradient: 'linear-gradient(135deg, #805ad5 0%, #d53f8c 100%)',
    patternId: 'unicorns.svg',
    patternSize: '600px',
    foregroundColor: '#ffffff',
    accentColor: '#f093fb',
  },
  {
    id: 'snowflake',
    name: 'Arctic Minimal',
    type: ExperienceStyles.Scene,
    color: { hue: 210, saturation: 50, lightness: 80 }, // light blue
    gradient: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
    patternId: 'confetti-pattern.svg',
    patternSize: '600px',
    foregroundColor: '#2d3748',
    accentColor: '#4299e1',
  },
  {
    id: 'notebook',
    name: 'Composition Notebook',
    type: ExperienceStyles.Scene,
    color: { hue: 0, saturation: 0, lightness: 20 }, // dark gray
    gradient: 'linear-gradient(135deg, #000000 0%, #2e2e2eff 100%)',
    patternId: 'notebook.jpg',
    patternSize: '300px',
    foregroundColor: '#000000',
    accentColor: '#000',
  },
];

// Pattern Styles Configuration
export const patternStyles: PatternStyle[] = [
  {
    id: 'school',
    name: 'Back to School',
    type: ExperienceStyles.Pattern,
    gradient: `
      linear-gradient(180deg, #48A175 0%, rgba(72, 161, 117, 0.50) 100%), #FFF
    `,
    color: { hue: 160, saturation: 70, lightness: 60 }, // #48A175
    colors: ['#48A175', 'rgba(72, 161, 117, 0.50)'],
    direction: 180,
    patternId: 'school.svg',
    patternSize: '400px',
  },
  {
    id: 'confetti',
    name: 'Confetti',
    type: ExperienceStyles.Pattern,
    gradient: 'linear-gradient(180deg, #4693D5 0%, #B5D4EF 100%)',
    color: { hue: 210, saturation: 100, lightness: 60 }, // blue
    colors: ['#4693D5', '#B5D4EF'],
    patternId: 'confetti-pattern.svg',
    patternSize: '500px',
  },
  {
    id: 'rocket',
    name: 'Business',
    type: ExperienceStyles.Pattern,
    gradient:
      'linear-gradient(180deg, #919191 0%, rgba(145, 145, 145, 0.50) 100%)',
    color: { hue: 0, saturation: 0, lightness: 57 }, // #919191
    colors: ['#919191', 'rgba(145, 145, 145, 0.50)'],
    patternId: 'business.svg',
    patternSize: '400px',
  },
  {
    id: 'pets',
    name: 'Pets',
    type: ExperienceStyles.Pattern,
    gradient: `linear-gradient(180deg, #745FFF 0%, rgba(116, 95, 255, 0.50) 100%)`,
    color: { hue: 250, saturation: 100, lightness: 70 }, // #745FFF
    colors: ['#745FFF', 'rgba(116, 95, 255, 0.50)'],
    patternId: 'pets.svg',
    patternSize: '400px',
  },
  {
    id: 'travel',
    name: 'Travel',
    type: ExperienceStyles.Pattern,
    gradient: `linear-gradient(180deg, #48A175 0%, rgba(72, 161, 117, 0.50) 100%)`,
    color: { hue: 160, saturation: 70, lightness: 60 }, // #48A175
    colors: ['#48A175', 'rgba(72, 161, 117, 0.50)'],
    patternId: 'travel.svg',
    patternSize: '900px',
  },
  {
    id: 'minimal',
    name: 'Minimal',
    type: ExperienceStyles.Pattern,
    gradient: `linear-gradient(180deg, #919191 0%, rgba(145, 145, 145, 0.50) 100%)`,
    colors: ['#919191', 'rgba(145, 145, 145, 0.50)'],
    color: { hue: 0, saturation: 0, lightness: 57 }, // #919191
    patternId: 'minimal.svg',
    patternSize: '1000px',
  },
  {
    id: 'flower',
    name: 'Flowers',
    type: ExperienceStyles.Pattern,
    gradient: `linear-gradient(180deg, #EF5693 0%, rgba(239, 86, 147, 0.50) 100%)`,
    colors: ['#EF5693', 'rgba(239, 86, 147, 0.50)'],
    color: { hue: 340, saturation: 100, lightness: 70 }, // pink
    patternId: 'flowers.svg',
    patternSize: '1200px',
  },
  {
    id: 'heart',
    name: 'Heartfelt',
    type: ExperienceStyles.Pattern,
    gradient: `
    linear-gradient(180deg, #F13538 0%, rgba(241, 53, 56, 0.50) 100%), #FFF;
    `,
    color: { hue: 0, saturation: 100, lightness: 70 }, // red
    colors: ['#F13538', 'rgba(241, 53, 56, 0.50)'],
    patternId: 'hearts.svg',
    patternSize: '600px',
  },
  {
    id: 'unicorn',
    name: 'Unicorns and Rainbows',
    type: ExperienceStyles.Pattern,
    color: { hue: 250, saturation: 100, lightness: 70 }, // #745FFF
    gradient: `linear-gradient(180deg, #745FFF 0%, rgba(116, 95, 255, 0.50) 100%)`,
    patternId: 'unicorns.svg',
    patternSize: '900px',
  },
];

// Build a lookup map of all styles at module load time
const styleMap: Record<string, StyleItem> = {};

[
  ...colorSwatches,
  ...gradientSwatches,
  ...blockStyles,
  ...sceneStyles,
  ...patternStyles,
].forEach((item) => {
  styleMap[item.id] = item;
});

/**
 * Returns the style type ('Color' | 'Gradient' | 'Block' | 'Scene' | 'Pattern') for a given style id.
 * Returns undefined if not found.
 */
export function getStyleTypeById(
  id: string
): BaseStyleItem['type'] | undefined {
  return styleMap[id]?.type;
}

/**
 * Returns the full style object for a given style id.
 * Returns undefined if not found.
 */
export function getStyleById(id: string): StyleItem | undefined {
  return styleMap[id];
}
